import { useDispatch } from 'react-redux'

import { configureStore } from '@reduxjs/toolkit'
import { QueryCacheKey, setupListeners } from '@reduxjs/toolkit/query'
import { createLogger } from 'redux-logger'
import { PersistConfig, PersistedState, persistReducer, persistStore } from 'redux-persist'
import getStoredStateMigrateV4 from 'redux-persist/lib/integration/getStoredStateMigrateV4'
import storage from 'redux-persist/lib/storage'
import { encryptTransform } from 'redux-persist-transform-encrypt'
import promise from 'redux-promise-middleware'

import autoThunkMiddleware from '@lyrahealth-inc/shared-app-logic/src/features/common/redux-middleware/autoThunkMiddleware'

import { baseApi } from './baseApi'
import { createPersistScopedReducerMiddleware } from './persistScope/persistScopedReducer'
import rootReducer from './reducers'
import { track } from '../../../mixpanel/mixpanelTracking'
import { actionAlertHandler, actionStyles } from '../common/constants/reduxConstants'
import { axiosAuthInstance } from '../common/http/axiosInstance'
import { logToSentry } from '../common/utils/utils'

const encryptor = encryptTransform({
  secretKey: 'lhpp-enc',
})

const autoThunk = autoThunkMiddleware({
  httpClient: axiosAuthInstance,
  errorHandler: (error: any, dispatch: any, action: any) =>
    actionAlertHandler({ actionStyle: actionStyles.ERROR, message: error, action, dispatch }),
  track,
  log: logToSentry,
})
const isProduction = process.env.NODE_ENV === 'production'

const persistConfig: PersistConfig<ReturnType<typeof rootReducer>> = {
  key: 'root',
  storage: storage,
  whitelist: ['auth', 'healthPlan', 'privacyGuard', 'informedConsentBanner'],
  transforms: [encryptor],
  getStoredState: getStoredStateMigrateV4({
    whitelist: ['auth', 'healthPlan'],
    transforms: [encryptor],
  }) as (config: PersistConfig<any>) => Promise<PersistedState>,
}

const persistedReducer = persistReducer(persistConfig, rootReducer)

const initialState = {}
const store = configureStore({
  reducer: persistedReducer,
  preloadedState: initialState,
  middleware: (getDefaultMiddleware) => {
    const sharedMiddleware = getDefaultMiddleware({ serializableCheck: false, immutableCheck: false }).concat(
      promise,
      autoThunk,
      baseApi.middleware,
      createPersistScopedReducerMiddleware<ReturnType<typeof rootReducer>>()('userSettings', {
        getScopeKey: (state) => state.userSettings.scope?.id,
        getEncryptionKey: (state) => state.userSettings.scope?.encryptionKey,
      }),
      createPersistScopedReducerMiddleware<ReturnType<typeof rootReducer>>()('api', {
        getScopeKey: (state) => state.userSettings.scope?.id,
        getEncryptionKey: (state) => state.userSettings.scope?.encryptionKey,
        getHydrationContext: (state) => state.apiMeta.persistedEndpointVersions,
        shouldPersist: (state) => state.apiMeta.shouldPersist,
        transformState: (state, hydrationContext) => {
          // Remove the queries from the state
          const queriesToPersist = state.provided.PERSIST?.__internal_without_id ?? []
          const filteredQueries = Object.fromEntries(
            Object.entries(state.queries ?? {})
              .filter(([key, value]) => value?.data && queriesToPersist.includes(key as QueryCacheKey))
              .map(([key, value]) => [
                key,
                {
                  ...value,
                  status: 'fulfilled',
                  fulfilledTimeStampDuringPersist: value?.fulfilledTimeStamp,
                  // If the query is persisted, we need to set the version to the persisted version
                  // Otherwise, we need to set it to the current version
                  hydrationVersion: value?.endpointName ? (hydrationContext?.[value?.endpointName] ?? 0) : 0,
                },
              ]),
          )
          const filteredQueryKeys = Object.keys(filteredQueries)
          const filteredProvided = Object.entries(state.provided)
            .filter(([_, value]) =>
              value.__internal_without_id?.some((queryKey: QueryCacheKey) => filteredQueryKeys.includes(queryKey)),
            )
            .reduce((acc, [key, value]) => {
              acc[key] = value
              return acc
            }, {} as any)
          return { queries: filteredQueries, provided: filteredProvided }
        },
      }),
    )
    if (isProduction) {
      return sharedMiddleware
    }
    return sharedMiddleware.concat(
      // Some typing issues with the logger and autoThunk
      // @ts-ignore-next-line
      createLogger({
        collapsed: true,
      }),
    )
  },
})

setupListeners(store.dispatch)

export type AppDispatch = typeof store.dispatch
export type RootState = ReturnType<typeof store.getState>
export const useAppDispatch: () => AppDispatch = () => useDispatch<AppDispatch>()

// Export the store to Cypress to give access to it in the tests.
if (window.Cypress) {
  ;(window as any).store = store
}

export const persistor = persistStore(store)
export default store
